"use client"

// Issue #70 — "Add a card of the client for charging to the connected
// Stripe account and process payment."
//
// Self-contained panel: lists saved cards for a cleaning customer, lets
// the tenant add a new card via Stripe Elements (collected on-session,
// stored off-session on the tenant's connected Stripe account), and
// optionally charge a saved card.
//
// Everything Stripe-side runs through the tenant's connected account via
// the backend endpoints at /api/customers/:id/stripe/*. No card tokens
// ever hit our DB.
//
// Props:
//   customerId   — numeric id of the SF cleaning customer
//   compact      — true: hide the "Charge card" button and chrome; useful
//                  for the Customer card on job details
//   onCharge     — (optional) called when the user clicks Charge. Instead
//                  of asking for an amount here, hand off to the invoice
//                  flow in the parent if provided.
//
// Public helpers (so a parent can do the charge itself with its own
// amount / invoice context):
//   customerCardsAPI.charge(customerId, { amount, paymentMethodId, ... })

import { useCallback, useEffect, useMemo, useState } from "react"
import { CreditCard, Plus, Trash2, Loader2, AlertTriangle, CheckCircle2, X } from "lucide-react"
import { loadStripe } from "@stripe/stripe-js"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { customerCardsAPI } from "../services/api"

// Stripe instances are keyed by (publishableKey, stripeAccount) — a single
// browser session may touch multiple tenants in dev, so we cache per key
// pair instead of a module-level singleton. stripeAccount is only passed
// for Model A (Connect); Model B (tenant-direct keys) must not include it.
const stripeCache = new Map()
function getStripeFor(publishableKey, stripeAccount) {
  const key = `${publishableKey}::${stripeAccount || "direct"}`
  if (!stripeCache.has(key)) {
    stripeCache.set(key, stripeAccount
      ? loadStripe(publishableKey, { stripeAccount })
      : loadStripe(publishableKey))
  }
  return stripeCache.get(key)
}

const brandLabel = (b) => {
  if (!b) return "Card"
  const l = String(b).toLowerCase()
  if (l === "visa") return "Visa"
  if (l === "mastercard") return "Mastercard"
  if (l === "amex") return "Amex"
  if (l === "discover") return "Discover"
  return b[0].toUpperCase() + b.slice(1)
}

export default function CustomerStripeCards({ customerId, compact = false, onCharge }) {
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [addOpen, setAddOpen] = useState(false)

  const reload = useCallback(async () => {
    if (!customerId) return
    setLoading(true); setError("")
    try {
      const list = await customerCardsAPI.list(customerId)
      setCards(list)
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Could not load cards")
      setCards([])
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => { reload() }, [reload])

  const handleDetach = async (pmId) => {
    if (!window.confirm("Remove this card from the customer?")) return
    try {
      await customerCardsAPI.detach(customerId, pmId)
      await reload()
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Could not remove card")
    }
  }

  return (
    <div>
      {!compact && (
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-4 h-4 text-[var(--sf-ink-3)]" />
          <div className="text-[13px] font-semibold text-[var(--sf-ink)]">Cards on file</div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-md border border-[var(--sf-border-soft)] bg-white hover:bg-[var(--sf-panel-soft)] text-[var(--sf-ink)]"
          >
            <Plus className="w-3.5 h-3.5" />
            Add card
          </button>
        </div>
      )}

      {error && (
        <div className="mb-2 px-3 py-2 rounded-md text-[12px] bg-[var(--sf-red-soft,#fef2f2)] border border-[var(--sf-red,#dc2626)]/30 text-[var(--sf-red-dark,#991b1b)] flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-3 text-[12px] text-[var(--sf-ink-3)]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading cards…
        </div>
      ) : cards.length === 0 ? (
        <div className="py-3 text-[12px] text-[var(--sf-ink-3)]">
          No cards saved.{compact && " "}
          {compact && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="text-[var(--sf-blue-500)] font-semibold hover:underline"
            >
              Add card
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {cards.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 px-3 py-2 rounded-md border border-[var(--sf-border-soft)] bg-white"
            >
              <CreditCard className="w-4 h-4 text-[var(--sf-ink-3)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-[var(--sf-ink)]">
                  {brandLabel(c.brand)} •••• {c.last4}
                  {c.isDefault && (
                    <span className="ml-2 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--sf-blue-500)]">Default</span>
                  )}
                </div>
                <div className="text-[11px] text-[var(--sf-ink-3)]">
                  Expires {String(c.expMonth).padStart(2, "0")}/{String(c.expYear).slice(-2)}
                </div>
              </div>
              {onCharge && (
                <button
                  type="button"
                  onClick={() => onCharge(c)}
                  className="text-[11px] font-semibold px-2 py-1 rounded-md text-[var(--sf-blue-500)] hover:bg-[var(--sf-panel-soft)]"
                >
                  Charge
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDetach(c.id)}
                title="Remove card"
                className="p-1.5 rounded-md text-[var(--sf-ink-3)] hover:bg-[var(--sf-panel-soft)] hover:text-[var(--sf-red-dark,#991b1b)]"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {compact && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-md border border-dashed border-[var(--sf-border-soft)] bg-transparent hover:bg-[var(--sf-panel-soft)] text-[var(--sf-ink-2)] self-start"
            >
              <Plus className="w-3.5 h-3.5" />
              Add another card
            </button>
          )}
        </div>
      )}

      <AddCardModal
        open={addOpen}
        customerId={customerId}
        onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); reload() }}
      />
    </div>
  )
}

// ───────────── Add-card modal ─────────────

function AddCardModal({ open, customerId, onClose, onDone }) {
  const [prep, setPrep] = useState(null) // { clientSecret, stripeAccountId, publishableKey }
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      setLoading(true); setError(""); setPrep(null)
      try {
        const data = await customerCardsAPI.createSetupIntent(customerId)
        if (cancelled) return
        if (!data.publishableKey) {
          setError("Stripe publishable key is not configured on this backend.")
          return
        }
        setPrep(data)
      } catch (e) {
        if (cancelled) return
        setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Could not start card setup")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [open, customerId])

  if (!open) return null

  const stripePromise = prep ? getStripeFor(prep.publishableKey, prep.stripeAccountId) : null

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3 flex-shrink-0">
          <h3 className="text-lg font-bold text-[var(--sf-text-primary,#0f172a)] flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[var(--sf-blue-500)]" />
            Add card
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--sf-text-muted,#94a3b8)] hover:text-[var(--sf-text-primary,#0f172a)] p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body — Stripe Elements (esp. Link's "save my info"
            section) can grow taller than the viewport on short screens,
            and the modal was clipping the Save/Cancel buttons below the
            fold. */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading && (
            <div className="flex items-center gap-2 py-6 text-[12.5px] text-[var(--sf-text-muted)]">
              <Loader2 className="w-4 h-4 animate-spin" />
              Preparing secure card form…
            </div>
          )}

          {error && (
            <div className="mb-3 px-3 py-2 rounded-md text-[12.5px] bg-[var(--sf-red-soft,#fef2f2)] border border-[var(--sf-red,#dc2626)]/30 text-[var(--sf-red-dark,#991b1b)] flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {prep && stripePromise && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret: prep.clientSecret, appearance: { theme: "stripe" } }}
            >
              <AddCardForm onDone={onDone} onCancel={onClose} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  )
}

function AddCardForm({ onDone, onCancel }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true); setError("")
    const { error: err, setupIntent } = await stripe.confirmSetup({
      elements,
      // No return_url — we handle 3DS inline on the dashboard.
      redirect: "if_required",
    })
    if (err) {
      setError(err.message || "Card could not be saved")
      setSubmitting(false)
      return
    }
    if (setupIntent && setupIntent.status === "succeeded") {
      setSuccess(true)
      setTimeout(() => onDone?.(), 600)
    } else {
      setError(`Card not saved (status: ${setupIntent?.status || "unknown"})`)
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <div className="px-3 py-2 rounded-md text-[12.5px] bg-[var(--sf-red-soft,#fef2f2)] border border-[var(--sf-red,#dc2626)]/30 text-[var(--sf-red-dark,#991b1b)] flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="px-3 py-2 rounded-md text-[12.5px] bg-[var(--sf-green-soft,#f0fdf4)] border border-[var(--sf-green,#16a34a)]/30 text-[var(--sf-green-dark,#166534)] flex items-start gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Card saved.</span>
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 text-sm text-[var(--sf-text-secondary,#475569)] bg-white border border-[var(--sf-border-light,#e2e8f0)] rounded-md hover:bg-[var(--sf-bg-page,#f8fafc)] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[var(--sf-blue-500)] rounded-md hover:opacity-90 disabled:opacity-50"
        >
          {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {submitting ? "Saving…" : "Save card"}
        </button>
      </div>
    </form>
  )
}
